import { DebugEventListener, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, BehaviorSubject, distinctUntilChanged, filter, map, Subject, finalize, switchMap, combineLatest, zip, pairwise, startWith, of, take } from 'rxjs';

import { DateTime } from 'luxon';

import { SharedSettings } from '../models/shared-settings';
import { url } from '../helpers/url';

import { EntityActionType } from '../models/entity-action-type';
import { ShiftStatus, WorkedShift } from '../models/worked-shift';
import { ValidationSummary } from '../models/validation-summary';
import { WeekLockStatus } from '../models/week-lock-status';
import { HubService } from '@shared/authentication/hub.service';
import { WeekLockState } from '@shared/models/week-lock-state';
import { arraysMatch } from '@shared/helpers/arrays-match';
import { LoggingService } from './logging.service';

interface ShiftListener {
    subject: Subject<WorkedShift[]>;
    condition: (ws: WorkedShift) => boolean;
};

@Injectable({
    providedIn: 'root'
})
export class WorkedShiftsService {

    private _connectionStatus = new BehaviorSubject<'connected' | 'disconnected' | 'reconnecting'>('disconnected');

    /// <summary>
    /// SignalR hub connection status; shown as a tooltip on the copyright for debugging purposes.
    /// </summary>
    public get ConnectionStatus(): Observable<'connected' | 'disconnected' | 'reconnecting'>
    {
        return this._connectionStatus.asObservable();
    }

    private weekLocks: BehaviorSubject<WeekLockStatus[]> = new BehaviorSubject<WeekLockStatus[]>([]);
    private workedShifts: BehaviorSubject<WorkedShift[]> = new BehaviorSubject<WorkedShift[]>([]);

    private activeListeners: ShiftListener[] = [];

    constructor(private settings: SharedSettings, private http: HttpClient, private hubService: HubService, private logger: LoggingService) {

        // update followers
        combineLatest([this.workedShifts, this.weekLocks]).subscribe(([workedShifts, weekLocks]) => {
            const withLocks: WorkedShift[] = workedShifts.map(ws => {
                const week = weekLocks.find(l => l.siteId === ws.siteId && ws.date >= l.propakWeek.weekStart && ws.date <= l.propakWeek.weekEnd);
                return {
                    id: ws.id,
                    siteId: ws.siteId,
                    date: ws.date,
                    shiftId: ws.shiftId,
                    shift: ws.shift,
                    shiftStatus: week?.weekLockState === WeekLockState.Locked ? ShiftStatus.Locked : ws.shiftStatus,
                    documentIds: ws.documentIds
                };
            });

            this.activeListeners.forEach(al => {
                al.subject.next(withLocks.filter(s => al.condition(s)));
            })
        });

        // track weeks for tracked shifts
        this.workedShifts.pipe(
            filter(workedShifts => workedShifts?.length > 0),
            distinctUntilChanged((prev, cur) => arraysMatch(prev, cur, (a, b) => a.id === b.id)),
            switchMap(workedShifts => {
                const join = (ws: WorkedShift, wl: WeekLockStatus) => ws.siteId === wl.siteId
                    && ws.date >= wl.propakWeek.weekStart
                    && ws.date <= wl.propakWeek.weekEnd;

                const keepWeeks = this.weekLocks.value.filter(wl => workedShifts.some(ws => join(ws, wl)));
                const shiftsWithMissingWeeks = workedShifts.filter(ws => !keepWeeks.some(wl => join(ws, wl)));

                const getRanges = shiftsWithMissingWeeks.reduce((acc, cur) => {
                    acc[cur.siteId] = acc[cur.siteId]
                        ? { min: DateTime.min(acc[cur.siteId].min, cur.date), max: DateTime.max(acc[cur.siteId].max, cur.date) }
                        : { min: cur.date, max: cur.date};
                    return acc;
                }, {} as { [siteId: string]: { min: DateTime, max: DateTime } });

                return zip([
                    of(keepWeeks),
                    zip(Object.entries(getRanges)
                        .map(([siteId, range]) => this.http.get<WeekLockStatus[]>(this.settings.apiBaseUrl
                            + url`workedShifts/locks/${siteId}/dates?start=${range.min.toISODate()}&end=${range.max.toISODate()}`)))
                ]);
            })
        ).subscribe(([keep, results]) => {
            this.weekLocks.next([
                ...keep,
                ...results.flat(1).map(s => {
                    s.propakWeek.weekStart = DateTime.fromISO(<any>s.propakWeek.weekStart);
                    s.propakWeek.weekEnd = DateTime.fromISO(<any>s.propakWeek.weekEnd);
                    return s;
                })
            ]);
        });

        const connection = this.hubService.connect('ShiftStatus');

        connection.on('UpdateWorkedShift', (workedShift: WorkedShift, action: EntityActionType) => {
            workedShift.date = DateTime.fromISO(<any>workedShift.date);

            this.updateShifts(
                action === EntityActionType.Remove ? [] : [workedShift],
                ws => ws.id === workedShift.id);
        });

        connection.on('UpdateSiteWeek', (weekLockStatus: WeekLockStatus, action: EntityActionType) => {
            weekLockStatus.propakWeek.weekStart = DateTime.fromISO(<any>weekLockStatus.propakWeek.weekStart);
            weekLockStatus.propakWeek.weekEnd = DateTime.fromISO(<any>weekLockStatus.propakWeek.weekEnd);

            this.weekLocks.next([
                ...this.weekLocks.value.filter(wls => wls.id !== weekLockStatus.id),
                ...(action == EntityActionType.Remove ? [] :  [weekLockStatus])
            ]);
        });

        connection.onreconnecting(() => this._connectionStatus.next('reconnecting'));
        connection.onreconnected(() => this._connectionStatus.next('connected'));
        connection.onclose(() => this._connectionStatus.next('disconnected'));

        connection.start()
            .then(() => this._connectionStatus.next('connected'))
            .catch(err => this.logger.error(err));
    }

    private createListener(condition: (shift: WorkedShift) => boolean): Observable<WorkedShift[]> {
        const listener = {
            subject: new Subject<WorkedShift[]>(),
            condition
        };

        this.activeListeners.push(listener);

        return listener.subject.pipe(
            finalize(() => {
                this.activeListeners.splice(this.activeListeners.indexOf(listener), 1);
            }),
            distinctUntilChanged((prev, cur) => arraysMatch(prev, cur, (a, b) => this.workedShiftsMatch(a, b))));
    }

    private updateShifts(updatedShifts: WorkedShift[], remCondition: (shift: WorkedShift) => boolean): void {
        const currentShifts = this.workedShifts.value;

        const allShifts = [
            ...currentShifts.filter(s => !remCondition(s)),
            ...updatedShifts
        ];

        const followedShifts = allShifts.filter(s => this.activeListeners.some(l => l.condition(s)));

        this.workedShifts.next(followedShifts);
    }

    private workedShiftsMatch(x: WorkedShift, y: WorkedShift): boolean {
        return x.id === y.id
            && x.siteId === y.siteId
            && x.date.equals(y.date)
            && x.shiftId === y.shiftId
            && x.shiftStatus === y.shiftStatus;
    }

    FollowById(id: number): Observable<WorkedShift> {
        this.UpdateById(id);

        return this.createListener(s => s.id === id).pipe(
            filter(ws => !!ws.length),
            map(ws => ws[0]));
    }

    FollowByCalendarMonth(siteId: string, month: number, year: number): Observable<WorkedShift[]> {
        this.UpdateByMonth(siteId, month, year);

        return this.createListener(s => s.siteId === siteId
            && s.date.month === month
            && s.date.year === year);
    }

    FollowByDate(siteId: string, date: DateTime): Observable<WorkedShift[]> {
        this.UpdateByDate(siteId, date);

        return this.createListener(s => s.siteId === siteId
            && s.date.equals(date));
    }

    FollowByDateRange(siteId: string, startDate: DateTime, endDate: DateTime): Observable<WorkedShift[]> {
        this.UpdateByDateRange(siteId, startDate, endDate);

        return this.createListener(s => s.siteId === siteId
            && s.date >= startDate
            && s.date <= endDate);
    }

    PeekById(id: number): Observable<WorkedShift | null> {
        return this.workedShifts.pipe(
            map(workedShifts => workedShifts.find(ws => ws.id === id)),
            filter(ws => !!ws),
            take(1));
    }

    private UpdateById(id: number): void {
        if (!id)
        {
            if (!this.settings.isProduction)
                throw new Error('WorkedShiftsService.UpdateById: id was null!');
            return;
        }

        this.http.get<WorkedShift>(this.settings.apiBaseUrl + url`workedshifts/${id}`)
            .subscribe(updated => {
                updated.date = DateTime.fromISO(<any>updated.date);

                this.updateShifts([updated], ws => ws.id === id);
            });
    }

    private UpdateByMonth(siteId: string, month: number, year: number): void {
        this.http.get<WorkedShift[]>(this.settings.apiBaseUrl + url`workedshifts/dates?siteId=${siteId}&month=${month}&year=${year}`)
            .subscribe(updated => {
                updated.forEach(ws => ws.date = DateTime.fromISO(<any>ws.date));

                this.updateShifts(updated, ws =>
                    ws.siteId === siteId &&
                    ws.date.month === month &&
                    ws.date.year === year);
            });
    }

    private UpdateByDate(siteId: string, date: DateTime): void {
        this.http.get<WorkedShift[]>(this.settings.apiBaseUrl + url`workedshifts/search?siteId=${siteId}&date=${date.toISODate()}`)
            .subscribe(updated => {
                updated.forEach(ws => ws.date = DateTime.fromISO(<any>ws.date));

                this.updateShifts(updated, ws =>
                    ws.siteId === siteId &&
                    ws.date.equals(date));
            });
    }

    private UpdateByDateRange(siteId: string, startDate: DateTime, endDate: DateTime): void {
        this.http.get<WorkedShift[]>(this.settings.apiBaseUrl
            + url`workedshifts/search?siteId=${siteId}&startDate=${startDate.toISODate()}&endDate=${endDate.toISODate()}`)
            .subscribe(updated => {
                updated.forEach(ws => ws.date = DateTime.fromISO(<any>ws.date));

                this.updateShifts(updated, ws => ws.siteId === siteId
                    && ws.date >= startDate
                    && ws.date <= endDate);
            });
    }

    GetByDateShift(siteId: string, date: DateTime, shiftId: number): Observable<WorkedShift | null> {
        return this.http.get<WorkedShift>(this.settings.apiBaseUrl
            + url`workedshifts?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}`)
            .pipe(map(workedShift => {
                if (!workedShift)
                    return null;
                workedShift.date = DateTime.fromISO(<any>workedShift.date);

                this.updateShifts([workedShift], ws => ws.id === workedShift.id);

                return workedShift;
            }));
    }
    GetLatestForSite(siteId: string): Observable<WorkedShift> {
        return this.http.get<WorkedShift>(this.settings.apiBaseUrl + url`workedShifts?siteId=${siteId}`)
            .pipe(map(workedShift => {
                if (!!workedShift) {
                    workedShift.date = DateTime.fromISO(<any>workedShift.date);

                    this.updateShifts([workedShift], ws => ws.id === workedShift.id);
                }
                return workedShift;
        }));
    }

    GetImmediateById(id: number): Observable<WorkedShift> {
        return this.http.get<WorkedShift>(this.settings.apiBaseUrl + url`workedshifts/${id}`)
            .pipe(map(workedShift => {
                workedShift.date = DateTime.fromISO(<any>workedShift.date);

                this.updateShifts([workedShift], ws => ws.id === workedShift.id);

                return workedShift;
            }));
    }


    OpenShift(siteId: string, date: DateTime, shiftId: number): Observable<WorkedShift> {
        return this.http.post<WorkedShift>(this.settings.apiBaseUrl + url`workedShifts`, {
            siteId,
            date: date.toISODate(),
            shiftId
        }).pipe(map(updated => {
            updated.date = DateTime.fromISO(<any>updated.date);

            this.updateShifts([updated], ws => ws.id === updated.id);

            return updated;
        }));
    }

    SetWorkedShiftStatus(id: number, status: ShiftStatus): Observable<WorkedShift> {
        return this.http.patch<WorkedShift>(this.settings.apiBaseUrl + url`workedShifts/${id}`, status)
            .pipe(map(updated => {
                updated.date = DateTime.fromISO(<any>updated.date);

                this.updateShifts([updated], ws => ws.id === updated.id);

                return updated;
            }));
    }

    DeleteWorkedShift(id: number, siteId: string): Observable<ValidationSummary | WorkedShift> {
        return this.http.delete<ValidationSummary | WorkedShift>(this.settings.apiBaseUrl + url`workedShifts/${id}?siteId=${siteId}`)
            .pipe(map(response => {
                // if response is WorkedShift, and thus success
                if (!response.hasOwnProperty('errors')) {
                    (<WorkedShift>response).date = DateTime.fromISO(<any>(<WorkedShift>response).date);

                    this.updateShifts([], ws => ws.id === id);
                }
                return response;
            }));
    }
}

